import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Chip,
  Stack,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  FileDownload as ExportIcon,
  Receipt as InvoiceIcon,
  Business as CompanyIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  DateRange as DateIcon,
  Tag as IdIcon,
} from "@mui/icons-material";
import { apiCall } from "../../services/ApiCall.js";
import config from "../../config/config.json";
import * as XLSX from 'xlsx';

const InvoiceDetails = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Original state variables (unchanged)
  const [data, setData] = useState([]);
  const [invoiceId, setInvoiceId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const primaryColor = "#0d6efd";

  // Original URL parsing logic (unchanged)
  useEffect(() => {
    const hash = window.location.hash;
    const regex = /\/invoice\/(\d+)/;
    const match = hash.match(regex);

    if (match) {
      setInvoiceId(match[1]);
    }
  }, []);

  // Original API function (unchanged logic)
  const getAllInvoice = async () => {
    setLoading(true);
    setError(null);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}getRequestsList`,
        {},
        { invoiceId, requestId: null },
        "GET"
      );
      if (isSuccess) {
        setData(data?.data?.reverse() || []);
      } else {
        setError("Failed to load invoice details");
      }
    } catch (err) {
      console.error("Error fetching invoice details:", err);
      setError("An error occurred while loading invoice details");
    }
    setLoading(false);
  };

  // Original useEffect (unchanged logic)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    } else {
      if (invoiceId) getAllInvoice();
    }
  }, [invoiceId]);

  // Original search handler (unchanged)
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Original filtering and sorting logic (unchanged)
  const filteredData = data
    .slice() // Create a shallow copy to avoid mutating the original data
    .sort((a, b) => new Date(b.approvedDate) - new Date(a.approvedDate)) // Sort by approvedDate in descending order
    .filter((invoice) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        `${invoice?.id}`.toLowerCase().includes(searchLower) ||
        `${invoice?.araId}`.toLowerCase().includes(searchLower) ||
        `${invoice?.approvedDate}`.toLowerCase().includes(searchLower) ||
        `${invoice?.companyName}`.toLowerCase().includes(searchLower) ||
        `${invoice?.siteAddress} ${invoice?.siteCity} ${invoice?.siteState}`.toLowerCase().includes(searchLower) ||
        `${invoice?.name}`.toLowerCase().includes(searchLower) ||
        `${invoice?.email}`.toLowerCase().includes(searchLower) ||
        `${invoice?.phone}`.toLowerCase().includes(searchLower)
      );
    });

  // Original export function (unchanged logic)
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredData.map(invoice => ({
        'MSI ID': invoice?.id,
        "ARA ID": invoice?.araId,
        "Approved Date": invoice?.approvedDate ? new Date(invoice.approvedDate).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }).replace(/\//g, '-') : '',
        Company: invoice?.companyName,
        Site: `${invoice?.siteAddress}, ${invoice?.siteCity}, ${invoice?.siteState}`,
        "Main Contact": invoice?.name,
        "Main Contact Email": invoice?.email,
        "Main Contact Phone": invoice?.phone,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");
    XLSX.writeFile(workbook, "Invoices.xlsx");
  };

  // Original date formatting function (unchanged)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).replace(/\//g, '-');
  };

  const LoadingSkeleton = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>ARA ID</TableCell>
            <TableCell>Approved Date</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Site</TableCell>
            <TableCell>Main Contact</TableCell>
            <TableCell>Main Contact Email</TableCell>
            <TableCell>Main Contact Phone</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(5)].map((_, index) => (
            <TableRow key={index}>
              <TableCell><Skeleton /></TableCell>
              <TableCell><Skeleton /></TableCell>
              <TableCell><Skeleton /></TableCell>
              <TableCell><Skeleton /></TableCell>
              <TableCell><Skeleton /></TableCell>
              <TableCell><Skeleton /></TableCell>
              <TableCell><Skeleton /></TableCell>
              <TableCell><Skeleton /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'transparent' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: primaryColor, mb: 1 }}>
              Invoice Details
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {invoiceId ? `Invoice ID: ${invoiceId}` : 'Detailed invoice information'}
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<ExportIcon />}
            onClick={exportToExcel}
            disabled={filteredData.length === 0}
            sx={{
              backgroundColor: primaryColor,
              '&:hover': {
                backgroundColor: primaryColor,
                opacity: 0.9,
              }
            }}
          >
            Export to Excel
          </Button>
        </Box>

        {/* Search Section */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Company, Site, Contact, or any field..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {filteredData.length} records found
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Data Table */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: `${primaryColor}08` }}>
                  <TableCell sx={{ fontWeight: 600, color: primaryColor, minWidth: 80 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IdIcon fontSize="small" />
                      ID
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: primaryColor, minWidth: 100 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IdIcon fontSize="small" />
                      ARA ID
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: primaryColor, minWidth: 140 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DateIcon fontSize="small" />
                      Approved Date
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: primaryColor, minWidth: 180 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CompanyIcon fontSize="small" />
                      Company
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: primaryColor, minWidth: 200 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon fontSize="small" />
                      Site
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: primaryColor, minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      Main Contact
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: primaryColor, minWidth: 200 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" />
                      Contact Email
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: primaryColor, minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon fontSize="small" />
                      Contact Phone
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData && filteredData.length > 0 ? (
                  filteredData.map((invoice) => (
                    <TableRow
                      key={invoice?.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: `${primaryColor}05`,
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={invoice?.id || '-'}
                          size="small"
                          sx={{
                            backgroundColor: `${primaryColor}15`,
                            color: primaryColor,
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {invoice?.araId || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(invoice?.approvedDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CompanyIcon sx={{ color: primaryColor, fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {invoice?.companyName || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary">
                            {invoice?.siteAddress ? 
                              `${invoice.siteAddress}, ${invoice.siteCity}, ${invoice.siteState}` : 
                              '-'
                            }
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
                          <Typography variant="body2">
                            {invoice?.name || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary">
                            {invoice?.email || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary">
                            {invoice?.phone || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <InvoiceIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                        <Typography variant="h6" color="text.secondary">
                          No invoice details found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {searchQuery ? 'Try adjusting your search criteria' : 'No data available for this invoice'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default InvoiceDetails;